import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@wellcheck/components/ui/button";

import { useLinkReissueStatus } from "../api/useLinkReissueStatus";

import { AnswerPeriodEndedScreen } from "./AnswerPeriodEndedScreen";
import { LinkReissueDialog } from "./LinkReissueDialog";

/**
 * 古いリンクは token 不一致で verify が answer_closed を付けないため、期限切れかどうかは
 * 対象確認 (ADR-0061 Decided Questions #8) の closed でこの画面の中から判定する。
 */
export interface InvalidLinkScreenProps {
  campaignId: string;
  respondentId: string;
}

export function InvalidLinkScreen({ campaignId, respondentId }: InvalidLinkScreenProps) {
  const { t } = useTranslation("respond");
  const [dialogOpen, setDialogOpen] = useState(false);
  const status = useLinkReissueStatus({ campaignId, respondentId });

  if (status.data?.status === "closed") {
    return <AnswerPeriodEndedScreen />;
  }

  const maskedEmail =
    status.data?.status === "eligible" || status.data?.status === "throttled"
      ? status.data.maskedEmail
      : undefined;
  const throttled = status.data?.status === "throttled";

  return (
    <section className="rounded-lg border border-border bg-card p-6 shadow-sm">
      <h1 className="text-base font-semibold">{t("session.invalidTitle")}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{t("session.invalidDescription")}</p>

      {maskedEmail ? (
        <div className="mt-4 space-y-2">
          <p className="text-sm text-muted-foreground">
            {t("linkReissue.prompt", { maskedEmail })}
          </p>
          <Button onClick={() => setDialogOpen(true)} disabled={throttled}>
            {t("linkReissue.trigger")}
          </Button>
          {throttled ? (
            <p className="text-sm text-muted-foreground">{t("linkReissue.throttled")}</p>
          ) : null}
          <LinkReissueDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            campaignId={campaignId}
            respondentId={respondentId}
            maskedEmail={maskedEmail}
          />
        </div>
      ) : null}
    </section>
  );
}
